import { CLE_HISTORIQUE, cleHistorique } from '@/components/historique/use-historique';
import { InterruptionIncompleteError, SessionExpireeError, getApi } from '@/lib/api';
import { rechercheVersLogin } from '@/lib/garde-session';
import { useToast } from '@/lib/notifications';
import { effacerSession, lireSession } from '@/lib/session';
import { retirerSuivi } from '@/lib/suivi-traductions';
import { cleTraduction } from '@/lib/use-lecture-traduction';
import type { ResumeTraduction, Traduction } from '@rosetta/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useRef, useState } from 'react';

/** The neutral toast once a translation is interrupted (and the card H7 shows it). */
export const MESSAGE_INTERROMPUE = 'Traduction interrompue.';

/**
 * The error toast when the task is cancelled but a sub-task refused: the translation reads as
 * interrupted, yet something may still be running in Tahiti.
 */
export const MESSAGE_INTERRUPTION_INCOMPLETE =
  "L'interruption n'a pas pu arrêter toutes les étapes.";

/** The error toast when Tahiti did not even cancel the task (a refusal, the service is down). */
export const MESSAGE_INTERRUPTION_ECHEC = "L'interruption n'a pas abouti, réessayez.";

/**
 * How an interruption ended:
 *  - `interrompue`: the task and its sub-tasks are cancelled;
 *  - `incomplete`: the task is cancelled, some sub-tasks are not (error toast);
 *  - `deja-terminee`: the translation had ended meanwhile, nothing was cancelled (no toast: the
 *    screen shows its new state);
 *  - `echec`: nothing was cancelled (error toast);
 *  - `session-expiree`: back to the login;
 *  - `ignoree`: an interruption of the same translation was already running.
 */
export type IssueInterruption =
  | 'interrompue'
  | 'incomplete'
  | 'deja-terminee'
  | 'echec'
  | 'session-expiree'
  | 'ignoree';

interface OptionsInterruption {
  /**
   * Leave for the history once the translation is interrupted (from the waiting screen, whose
   * translation is no longer worth showing). From a card of the history the user is already
   * there: the card turns into H7.
   */
  versHistorique: boolean;
}

/**
 * "interrompre" (#7), shared by the waiting screen W1 and the card of a running translation
 * H6: calls `interrompreTraduction`, then says how it went (the toast, the history, the
 * login) and tells the caller. `enCours` holds the translations being interrupted, to lock
 * their button. Confirming is the caller's job (W2): nothing is cancelled before.
 *
 * When the user leaves for the history, `enCours` is not cleared: the screen is going away,
 * and clearing it would let the (already cancelled) translation read for an instant as one
 * that is not being interrupted any more, and be announced twice.
 */
export function useInterruption({ versHistorique }: OptionsInterruption) {
  const toast = useToast();
  const navigate = useNavigate();
  const client = useQueryClient();
  const [enCours, setEnCours] = useState<ReadonlySet<string>>(() => new Set());
  // Closes the window between two confirmations before the state is rendered: each call cancels
  // paid work in Tahiti.
  const enVol = useRef(new Set<string>());

  const interrompre = useCallback(
    async (id: string): Promise<IssueInterruption> => {
      const session = lireSession();
      if (session === null) {
        void navigate({ to: '/login', search: rechercheVersLogin('session-expiree') });
        return 'session-expiree';
      }
      if (enVol.current.has(id)) return 'ignoree';
      enVol.current.add(id);
      setEnCours(new Set(enVol.current));

      let issue: IssueInterruption;
      try {
        issue = (await getApi().interrompreTraduction(session, id))
          ? 'interrompue'
          : 'deja-terminee';
      } catch (erreur) {
        if (erreur instanceof SessionExpireeError) issue = 'session-expiree';
        else if (erreur instanceof InterruptionIncompleteError) issue = 'incomplete';
        else issue = 'echec';
      }

      const quitte = versHistorique && (issue === 'interrompue' || issue === 'incomplete');
      if (issue === 'interrompue' || issue === 'incomplete') {
        // The task is cancelled: the history and the translation say so at once, and are read
        // again to settle on what Tahiti says.
        client.setQueryData<ResumeTraduction[]>(cleHistorique(session.utilisateur.id), (anciens) =>
          anciens?.map((resume) =>
            resume.id === id ? { ...resume, statut: 'interrompue' } : resume,
          ),
        );
        client.setQueryData<Traduction>(cleTraduction(id), (ancienne) =>
          ancienne === undefined ? undefined : { ...ancienne, statut: 'interrompue' },
        );
        void client.invalidateQueries({ queryKey: CLE_HISTORIQUE });
        // Nothing left to announce: the user asked for it.
        retirerSuivi(id);
        toast.afficher(
          issue === 'interrompue'
            ? { variant: 'neutre', message: MESSAGE_INTERROMPUE }
            : { variant: 'erreur', message: MESSAGE_INTERRUPTION_INCOMPLETE },
        );
        if (versHistorique) void navigate({ to: '/historique' });
      } else if (issue === 'deja-terminee') {
        void client.invalidateQueries({ queryKey: cleTraduction(id) });
        void client.invalidateQueries({ queryKey: CLE_HISTORIQUE });
      } else if (issue === 'session-expiree') {
        effacerSession();
        void navigate({ to: '/login', search: rechercheVersLogin('session-expiree') });
      } else {
        toast.afficher({ variant: 'erreur', message: MESSAGE_INTERRUPTION_ECHEC });
      }

      if (!quitte) {
        enVol.current.delete(id);
        setEnCours(new Set(enVol.current));
      }
      return issue;
    },
    [client, navigate, toast, versHistorique],
  );

  return { interrompre, enCours };
}
